import { useMediaQuery } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import Paper, { PaperProps } from '@material-ui/core/Paper'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import { addTourBanner, authSelectors } from 'ducks/auth'
import { chatDrawerActions, chatDrawerSelectors } from 'ducks/chatDrawer'
import { tourSelectors } from 'ducks/tour'
import { wsSelectors } from 'ducks/websocket'
import { Drawer } from 'opensolar-ui'

import { registerChatContext } from 'hooks/useAdaChat'
import { useAdaPositioning } from 'hooks/useAdaPositioning'
import { useDataProvider } from 'ra-core'
import { Resizable } from 're-resizable'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import Draggable from 'react-draggable'
import { useDropzone } from 'react-dropzone'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import appStorage from 'storage/appStorage'
import { RootState } from 'types/state'
import { useFeatureFlag } from 'util/featureFlags'
import { osFetch } from 'util/fetch'
import { resolveCurrentProjectId } from 'util/resolveCurrentProjectId'
import { useVoice } from 'voice/OpenAIRealtimeVoiceAPI/VoiceContext'
import TourBanner from '../../persistentContent/tours/ui3/TourBanner'
import { executeActions } from './adaChat/actions'
import AdaVoiceIndicator from './adaChat/AdaVoiceIndicator'
import { AttachedFile, ChatProvider } from './adaChat/ChatContext'
import ChatHeader from './adaChat/ChatHeader'
import ChatInput from './adaChat/ChatInput'
import ChatMessagesList from './adaChat/ChatMessagesList'
import { ChatScrollProvider } from './adaChat/ChatScrollContext'
import { CompactSuggestions } from './adaChat/CompactSuggestions'
import DisconnectedBanner from './adaChat/DisconnectedBanner'
import { useAreSuggestionsVisible } from './adaChat/hooks/useAreSuggestionsVisible'
import { useChatSuggestionsPreferences } from './adaChat/hooks/useChatSuggestionsPreferences'
import MessageFailedBanner from './adaChat/MessageFailedBanner'
import NetworkReconnectBanner from './adaChat/NetworkReconnectBanner'
import { useScrollToBottom } from './adaChat/useScrollToBottom'
import {
  hasUnuploadedFiles,
  MAX_FILE_SIZE,
  uploadSingleFileWithProgress,
  validateFiles,
} from './adaChat/utils/openAIFileUpload'
import VoiceCloseConfirmBanner from './adaChat/VoiceCloseConfirmBanner'
import {
  getLastMessage,
  getLastMessageIndex,
  isAwaitingResponse,
  shouldShowLoader,
  shouldShowRetryBanner,
  useRetryOnReconnect,
} from './ChatDrawer.hooks'
import { chatDrawerPaperSx, chatDrawerSx, useChatDrawerStyles } from './ChatDrawer.styles'

type AnchorPosition = 'left' | 'top' | 'right' | 'bottom'
type DisplayMode = 'drawer' | 'dialog'

const ChatDrawer = () => {
  const classes = useChatDrawerStyles()
  const translate = useTranslate()
  const dispatch = useDispatch()
  const isPro = useSelector(authSelectors.getIsAuthenticatedPro)
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('xs'))
  const history = useHistory()
  const { shouldElevate } = useAdaPositioning()
  const messages = useSelector(chatDrawerSelectors.getChatDrawerMessages)
  const actions = useSelector(chatDrawerSelectors.getAdaActions)
  const appState = useSelector(chatDrawerSelectors.getAppState)
  const [messageText, setMessageText] = useState('')
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [textInputEnabled, setTextInputEnabled] = useState(true)
  const [sendButtonEnabled, setSendButtonEnabled] = useState(true)

  const attachedFilesActions = React.useMemo(
    () => ({
      add: (file: AttachedFile) => {
        setAttachedFiles((prev) => [...prev, file])
      },
      remove: (index: number) => {
        setAttachedFiles((prev) => prev.filter((_, i) => i !== index))
      },
      update: (index: number, file: AttachedFile) => {
        setAttachedFiles((prev) => prev.map((f, i) => (i === index ? file : f)))
      },
      clear: () => {
        setAttachedFiles([])
      },
      set: (files: AttachedFile[]) => {
        setAttachedFiles(files)
      },
    }),
    []
  )
  // Currently the only way we know to get the orgId for a customer when viewing a share link is from the storage.
  const orgId = (useSelector(authSelectors.getOrgId) || window.getStorage().getItem('org_id')) as number
  const reduxProjectId = useSelector((state: RootState) => state.projectId)
  const projectId = resolveCurrentProjectId(history.location.pathname, reduxProjectId)
  const dataProvider = useDataProvider()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const lastAppStateSent = useRef({})
  const chatDrawerOpen = useSelector(chatDrawerSelectors.getChatDrawerOpen)
  const connectionId = useSelector(wsSelectors.getConnectionId)
  const [anchor, setAnchor] = useState<AnchorPosition>('right')
  const [displayMode, setDisplayMode] = useState<DisplayMode>('drawer')
  const [dimensions, setDimensions] = useState({ width: 600, height: 400 })
  const [conversationId, setConversationId] = useState(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const websocketAvailable = useSelector(wsSelectors.getAvailable)
  const osAssistantCode = isPro ? 'pro' : 'customer'
  const enableGeminiLiveAPI = !!window.localStorage.getItem('gemini-live-api-key')
  const {
    isSessionActive: voiceSessionActive,
    stopSession,
    isListening,
    isResponding,
    isPreparingResponse,
    reasoningStatus,
    speakingTranscript,
    toolCall,
  } = useVoice()
  const messagesNotSharedWithText = useSelector((state: RootState) =>
    chatDrawerSelectors.getMessagesNotSharedWithContext(state, 'text')
  )
  const [showVoiceCloseConfirm, setShowVoiceCloseConfirm] = useState(false)

  const isStreaming = useSelector(chatDrawerSelectors.getStreaming)
  const adaStatus = useSelector(chatDrawerSelectors.getAdaStatus)
  const sendFailed = useSelector(chatDrawerSelectors.getSendFailed)
  const tour = useSelector(tourSelectors.tour)
  const isInFujiTour = tour === 'fuji'

  const { messagesScrollRef, showScrollButton, hasNewMessages, handleScrollToBottom } = useScrollToBottom(messages)
  const { preferences } = useChatSuggestionsPreferences()
  const isFileDropEnabled = useFeatureFlag('ada_file_drop', 'on')

  const awaitingResponse = isAwaitingResponse(messages, isStreaming)

  const uploadFile = useCallback(
    async (file: File): Promise<AttachedFile> => {
      const newFile: AttachedFile = {
        file,
        uploadState: 'uploading',
        progress: 0,
      }
      attachedFilesActions.add(newFile)

      try {
        const result = await uploadSingleFileWithProgress(file, osAssistantCode, orgId, (progress) => {
          // Update progress as upload progresses
          if (progress === 100) {
            setAttachedFiles((prev) =>
              prev.map((f) => (f.file === file ? { ...f, progress: 100, uploadState: 'processing' as const } : f))
            )
          } else {
            setAttachedFiles((prev) => prev.map((f) => (f.file === file ? { ...f, progress } : f)))
          }
        })

        const updatedFile: AttachedFile = {
          ...newFile,
          uploadState: 'uploaded',
          uploadResult: result,
          progress: 100,
        }
        // Update the file by finding it using the file reference
        setAttachedFiles((prev) => prev.map((f) => (f.file === file ? updatedFile : f)))
        return updatedFile
      } catch (error) {
        const errorFile: AttachedFile = {
          ...newFile,
          uploadState: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        }
        // Update the file by finding it using the file reference
        setAttachedFiles((prev) => prev.map((f) => (f.file === file ? errorFile : f)))
        return errorFile
      }
    },
    [attachedFilesActions, osAssistantCode, orgId, setAttachedFiles]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const validationError = validateFiles(acceptedFiles)
        if (validationError) {
          console.error(validationError.message)
          return
        }

        const currentTotalSize = attachedFiles.reduce((sum, f) => sum + f.file.size, 0)
        const newFilesTotalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0)
        const newTotalSize = currentTotalSize + newFilesTotalSize

        if (newTotalSize > 4.5 * 1024 * 1024) {
          console.error(`Total file size exceeds 4.5MB limit`)
          return
        }

        acceptedFiles.forEach((file) => {
          uploadFile(file)
        })
      }
    },
    [attachedFiles, uploadFile]
  )

  const onDropRejected = useCallback((fileRejections: any[]) => {
    fileRejections.forEach(({ file, errors }) => {
      errors.forEach((error: any) => {
        if (error.code === 'file-too-large') {
          console.error(`File ${file.name} exceeds 4.5MB limit`)
        }
      })
    })
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: isFileDropEnabled ? onDrop : () => {},
    onDropRejected: isFileDropEnabled ? onDropRejected : () => {},
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE,
    disabled: !isFileDropEnabled,
  })

  const activeAutoDesignStates = React.useMemo(() => {
    const states: Array<{ toolExecutionState: any; messageIndex: number }> = []
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (
        message.toolExecutionState &&
        message.toolExecutionState.toolType === 'autoDesign' &&
        (message.toolExecutionState.status === 'running' ||
          message.toolExecutionState.status === 'ready' ||
          message.toolExecutionState.status === 'completed' ||
          message.toolExecutionState.status === 'error')
      ) {
        states.push({ toolExecutionState: message.toolExecutionState, messageIndex: i })
      }
    }
    return states
  }, [messages])

  const hasActiveActions = toolCall || activeAutoDesignStates.length > 0
  const voiceModeHeight = hasActiveActions ? 250 + activeAutoDesignStates.length * 80 + (toolCall ? 80 : 0) : 250

  const retryPending = useRetryOnReconnect(messages, orgId, conversationId, connectionId)

  const loaderVisible =
    shouldShowLoader(messages, isStreaming, adaStatus, retryPending, websocketAvailable) || isRetrying

  const areSuggestionsVisible = useAreSuggestionsVisible({
    messages,
    websocketAvailable,
    showLoader: loaderVisible,
    retryPending,
    sendFailed,
  })

  useEffect(() => {
    if (chatDrawerOpen) {
      dispatch(chatDrawerActions.markMessagesAsRead())

      const hasSeenAdaTour = window.localStorage.getItem('ada_chat_tour_seen')
      if (!hasSeenAdaTour) {
        dispatch(addTourBanner('ada_chat'))
        window.localStorage.setItem('ada_chat_tour_seen', 'true')
      }
    }
  }, [chatDrawerOpen, dispatch])

  // Reset isRetrying when retryPending clears or when streaming starts (retry worked)
  useEffect(() => {
    if ((!retryPending || isStreaming) && isRetrying) {
      setIsRetrying(false)
    }
  }, [retryPending, isRetrying, isStreaming])

  // Reset isRetrying when websocket disconnects during retry attempt
  useEffect(() => {
    if (!websocketAvailable && isRetrying) {
      setIsRetrying(false)
    }
  }, [websocketAvailable, isRetrying])

  // Chat header component
  const renderChatHeader = () => (
    <ChatHeader
      displayMode={displayMode}
      anchor={anchor}
      onDisplayModeChange={setDisplayMode}
      onAnchorChange={(position) => {
        setAnchor(position)
        // Update dimensions when changing layout
        if (position === 'bottom') {
          setDimensions((d) => ({ ...d, height: 380 }))
        } else {
          setDimensions((d) => ({ ...d, height: 500 }))
        }
      }}
      onClearChat={() => {
        messages.forEach((msg) => {
          if (msg.fileAttachments) {
            msg.fileAttachments.forEach((file) => {
              if (file.previewUrl) {
                URL.revokeObjectURL(file.previewUrl)
              }
            })
          }
        })
        setConversationId(null)
        setTextInputEnabled(true)
        dispatch(chatDrawerActions.clearMessages())
      }}
      onClose={handleCloseWithVoiceCheck}
      enableGeminiLiveAPI={enableGeminiLiveAPI}
    />
  )

  useEffect(() => {
    if (textInputEnabled && inputRef?.current) {
      inputRef.current.focus() // Regain focus when re-enabled
    }
  }, [textInputEnabled])

  useEffect(() => {
    if (actions.length > 0) {
      executeActions(actions as any, history, orgId, projectId, dataProvider, dispatch)
      dispatch(chatDrawerActions.clearActions())
    }
  }, [actions, dispatch, history, orgId, projectId, dataProvider])

  useEffect(() => {
    // TODO: This is a bit of a hack, could we handle this better through redux?
    // Basically, if the chat has no messages and a conversationId is set, we need to clear the conversationId
    // as this suggests we've cleared the chat elsewhere and need to reset the state.
    // This should only really occur in an org switch.
    if (messages.length === 0 && conversationId !== null) {
      setConversationId(null)
      lastAppStateSent.current = {}
      setMessageText('')
    }
  }, [messages.length, conversationId])

  const getChatContextUpdateMessageSuffix = (newState, oldState) => {
    /* Check if app state has changed. If so, inject updated state in the next message */
    // for each key, check the serialized value of the object
    let appStateKeysToUpdate: string[] = []
    let keys = new Set([...Object.keys(newState), ...Object.keys(oldState)])

    keys.forEach((key) => {
      if (JSON.stringify(newState[key]) !== JSON.stringify(oldState[key])) {
        appStateKeysToUpdate.push(`APP_STATE.${key}: ${JSON.stringify(newState[key])}`)
      }
    })

    if (appStateKeysToUpdate.length > 0) {
      if (window.adaDebug) console.log('APP_STATE UPDATE', appStateKeysToUpdate)
    }

    return appStateKeysToUpdate.length ? `\n===APP_STATE UPDATE=== \n${appStateKeysToUpdate.join('\n')}` : ''
  }

  const getUnsharedContextSuffix = () => {
    if (messagesNotSharedWithText.length === 0) {
      return ''
    }

    const contextMessages = messagesNotSharedWithText
      .map((msg) => `${msg.direction === 'send' ? 'User' : 'Assistant'}: ${msg.content}`)
      .join('\n')

    return `\n===PREVIOUS CONVERSATION CONTEXT===\n${contextMessages}\n===END CONTEXT===\n`
  }

  // Register chat context for useAdaChat hook
  useEffect(() => {
    registerChatContext({
      setMessageText,
      onSubmit: () => sendMessage(osAssistantCode, messageText, setMessageText, attachedFiles, attachedFilesActions),
      messageText,
    })
  }, [messageText, osAssistantCode, attachedFiles, attachedFilesActions])

  const sendMessage = async (
    osAssistantCode,
    message,
    setMessageText,
    attachedFiles: AttachedFile[] = [],
    attachedFilesActions: {
      add: (file: AttachedFile) => void
      remove: (index: number) => void
      update: (index: number, file: AttachedFile) => void
      clear: () => void
      set: (files: AttachedFile[]) => void
    }
  ) => {
    /**
     * Note that the POST message just sends the message and confirms it was sent, but the response is streamed back.
     *
     * Streaming: Create "send" message immediately when submitting, do not wait for response.
     * If sending fails, undo the "send" message and reload the value back into the input field.
     */

    // Block sending if there are files that aren't uploaded yet (unless they're removed)
    if (hasUnuploadedFiles(attachedFiles)) {
      return
    }

    // Store the original message for error recovery
    const originalMessage = message
    const originalFiles = [...attachedFiles]

    // Do not disable the input field if streaming is enabled, so they can start typing while waiting for the response.
    setSendButtonEnabled(false)

    const uploadedFiles = attachedFiles.filter((f) => f.uploadState === 'uploaded' && f.uploadResult)
    const fileAttachments = uploadedFiles.map((f) => {
      const isImage = f.file.type.startsWith('image/')
      const previewUrl = isImage ? URL.createObjectURL(f.file) : undefined
      return {
        file_id: f.uploadResult!.file_id,
        file_name: f.uploadResult!.file_name,
        file_type: f.uploadResult!.file_type,
        previewUrl,
      }
    })

    dispatch(chatDrawerActions.addMessage(message, 'send', 'text', fileAttachments))
    setMessageText('')
    attachedFilesActions.clear()

    const stateForMessage = {
      ...appState,
      pathname: history.location.pathname,
      project_id: projectId ?? appState.project_id ?? null,
    }
    const messageSuffixFromAppStateChange = getChatContextUpdateMessageSuffix(stateForMessage, lastAppStateSent.current)
    const unsharedContextSuffix = getUnsharedContextSuffix()

    // Mark voice messages as shared with text context
    if (messagesNotSharedWithText.length > 0) {
      dispatch(chatDrawerActions.markMessagesSharedWithContext('text'))
    }

    const fileIds = uploadedFiles.map((f) => f.uploadResult!.file_id)
    const fileNames = uploadedFiles.map((f) => f.uploadResult!.file_name)
    const fileTypes = uploadedFiles.map((f) => f.uploadResult!.file_type)

    osFetch(window.API_BASE_URL + `ada/${osAssistantCode}/message/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: 'Bearer ' + appStorage.getToken(),
      },
      body: JSON.stringify({
        message: unsharedContextSuffix + message + messageSuffixFromAppStateChange,
        conversation_id: conversationId,
        org_id: orgId,
        connection_id: connectionId,
        file_ids: fileIds.length > 0 ? fileIds : undefined,
        file_names: fileNames.length > 0 ? fileNames : undefined,
        file_types: fileTypes.length > 0 ? fileTypes : undefined,
      }),
    })
      .then((response) => {
        lastAppStateSent.current = stateForMessage

        if (!response.ok) {
          // Throw an error to trigger the catch block
          return response.json().then((errData) => {
            const error = new Error(`HTTP ${response.status}: ${errData?.message || response.statusText}`) as any
            error.responseData = errData
            throw error
          })
        }
        return response.json()
      })
      .then((data) => {
        dispatch(chatDrawerActions.clearSendFailed())
        const newConversationId = data.conversation_id
        if (newConversationId) {
          if (!conversationId) {
            setConversationId(newConversationId)
          }
        }
      })
      .catch((error) => {
        dispatch(chatDrawerActions.removeLastMessage())
        setMessageText(originalMessage)
        attachedFilesActions.set(originalFiles)
        dispatch(chatDrawerActions.setSendFailed())
      })
      .finally(() => {
        setTextInputEnabled(true)
        setSendButtonEnabled(true)
      })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault() // Prevents newline
      if (sendButtonEnabled) {
        if (messageText.trim()) {
          sendMessage(osAssistantCode, messageText, setMessageText, attachedFiles, attachedFilesActions)
        }
      }
    }
  }

  const handleRetrySend = useCallback(() => {
    if (!retryPending) return

    const targetMessageIndex = retryPending.messageIndex
    const lastMessageIndex = getLastMessageIndex(messages)
    const lastMessage = getLastMessage(messages)

    const hasLastMessage = !!lastMessage
    const lastMessageIsSend = lastMessage?.direction === 'send'
    const lastMessageMatchesRetryIndex = targetMessageIndex === lastMessageIndex
    const canRetry = hasLastMessage && lastMessageIsSend && lastMessageMatchesRetryIndex

    if (!canRetry) {
      return
    }

    setIsRetrying(true)

    // Clear the failed state for this message since we're retrying it
    dispatch(chatDrawerActions.clearMessageIndexFailed(targetMessageIndex))

    // Resend the last message
    const contentToResend = lastMessage.content
    dispatch(chatDrawerActions.removeLastMessage())
    sendMessage(osAssistantCode, contentToResend, setMessageText, attachedFiles, attachedFilesActions)
  }, [retryPending, messages, dispatch, osAssistantCode, attachedFiles, attachedFilesActions])

  const handleCloseWithVoiceCheck = useCallback(() => {
    if (voiceSessionActive) {
      setShowVoiceCloseConfirm(true)
    } else {
      dispatch(chatDrawerActions.setChatDrawerOpen(false))
    }
  }, [voiceSessionActive, dispatch])

  const handleVoiceCloseConfirm = useCallback(
    (endVoiceSession: boolean) => {
      setShowVoiceCloseConfirm(false)
      if (endVoiceSession) {
        stopSession()
        dispatch(chatDrawerActions.setVoiceSessionActive(false))
      }
      dispatch(chatDrawerActions.setChatDrawerOpen(false))
    },
    [dispatch, stopSession]
  )

  const renderContent = () => {
    const loaderVisible =
      shouldShowLoader(messages, isStreaming, adaStatus, retryPending, websocketAvailable) || isRetrying

    if (voiceSessionActive) {
      return (
        <div className={classes.voiceModeContent}>
          {showVoiceCloseConfirm ? (
            <VoiceCloseConfirmBanner
              compact={true}
              onKeepListening={() => handleVoiceCloseConfirm(false)}
              onEndSession={() => handleVoiceCloseConfirm(true)}
            />
          ) : (
            <AdaVoiceIndicator
              isListening={isListening}
              isResponding={isResponding}
              isPreparingResponse={isPreparingResponse}
              reasoningStatus={reasoningStatus}
              speakingTranscript={speakingTranscript}
              toolCall={toolCall}
              isSessionActive={voiceSessionActive}
            />
          )}
        </div>
      )
    }

    // Hide content when dragging files (only if file drop is enabled)
    if (isFileDropEnabled && isDragActive) {
      return null
    }

    return (
      <>
        {!websocketAvailable && <DisconnectedBanner />}
        <ChatScrollProvider scrollToBottom={handleScrollToBottom}>
          <ChatMessagesList
            messages={messages}
            showLoader={loaderVisible}
            showScrollButton={showScrollButton}
            hasNewMessages={hasNewMessages}
            onScrollToBottom={handleScrollToBottom}
            messagesContainerRef={messagesScrollRef}
            retryPending={retryPending}
            shouldElevate={shouldElevate}
            showCompactSuggestions={
              preferences.showCompactSuggestions &&
              messages.length > 0 &&
              !shouldShowRetryBanner(retryPending, websocketAvailable) &&
              !sendFailed &&
              websocketAvailable
            }
          />
        </ChatScrollProvider>
        {shouldShowRetryBanner(retryPending, websocketAvailable) && !isRetrying && <NetworkReconnectBanner />}
        {sendFailed && !awaitingResponse && !isStreaming && <MessageFailedBanner />}
        {preferences.showCompactSuggestions &&
          messages.length > 0 &&
          !shouldShowRetryBanner(retryPending, websocketAvailable) &&
          !sendFailed &&
          websocketAvailable && <CompactSuggestions />}
      </>
    )
  }

  const getTextPlaceholder = () => {
    if (voiceSessionActive) return 'Voice conversation active...'
    if (!websocketAvailable) return 'Chat disconnected...'
    if (areSuggestionsVisible) return 'Ask me anything'
    return 'What can I help you with?'
  }

  const textPlaceholder = translate(getTextPlaceholder())

  const renderInput = () => <ChatInput />

  // Local wrapper to close over dimensions and setDimensions
  const DialogPaperComponent = React.useCallback(
    React.forwardRef<HTMLDivElement, PaperProps>((props, ref) => {
      return (
        <Draggable
          nodeRef={ref as React.RefObject<HTMLDivElement>}
          handle="#draggable-dialog-title"
          cancel={'[class*="MuiDialogContent-root"]'}
        >
          <Resizable
            size={{ width: dimensions.width, height: dimensions.height }}
            onResizeStop={(e, direction, r, delta) => {
              setDimensions({
                width: dimensions.width + delta.width,
                height: dimensions.height + delta.height,
              })
            }}
            minWidth={300}
            minHeight={200}
            enable={{
              bottomRight: true,
              top: false,
              right: false,
              bottom: false,
              left: false,
              topRight: false,
              bottomLeft: false,
              topLeft: false,
            }}
            style={{ zIndex: 1500 }}
            handleStyles={{
              bottomRight: {
                width: '16px',
                height: '16px',
                bottom: '77px',
                right: '35px',
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
                position: 'absolute',
                cursor: 'se-resize',
                zIndex: 2000,
              },
            }}
          >
            <Paper
              {...props}
              ref={ref}
              style={{ width: '100%', height: '100%', padding: 0, overflow: 'visible', ...props.style }}
            />
          </Resizable>
        </Draggable>
      )
    }) as React.ForwardRefExoticComponent<PaperProps & React.RefAttributes<HTMLDivElement>>,
    [dimensions, setDimensions]
  )

  if (displayMode === 'dialog') {
    return (
      <Dialog
        open={chatDrawerOpen}
        hideBackdrop={true}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            // Do nothing, don't close
            return
          }
          handleCloseWithVoiceCheck()
        }}
        // @ts-expect-error
        PaperComponent={DialogPaperComponent}
        aria-labelledby="draggable-dialog-title"
        maxWidth={false}
        PaperProps={{
          style: {
            width: dimensions.width,
            height: voiceSessionActive ? voiceModeHeight : dimensions.height,
            minWidth: 400,
            minHeight: voiceSessionActive ? voiceModeHeight : 300,
            maxHeight: voiceSessionActive ? voiceModeHeight : undefined,
            backgroundColor: 'transparent',
            borderRadius: 16,
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            transition: 'height 0.3s ease-in-out, min-height 0.3s ease-in-out, max-height 0.3s ease-in-out',
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: '#ffffff',
            borderRadius: 16,
            position: 'relative',
          }}
          {...(isFileDropEnabled ? getRootProps() : {})}
        >
          {isFileDropEnabled && isDragActive && (
            <div className={classes.dragOverlayFull}>
              <div className={classes.dragOverlayText}>
                <CloudUploadIcon style={{ fontSize: 24 }} />
                Drop files here to upload
              </div>
            </div>
          )}
          <ChatProvider
            value={{
              messageText,
              setMessageText,
              onKeyDown: handleKeyDown,
              onSubmit: () =>
                sendMessage(osAssistantCode, messageText, setMessageText, attachedFiles, attachedFilesActions),
              onRetry: handleRetrySend,
              textInputEnabled,
              sendButtonEnabled,
              websocketAvailable,
              voiceSessionActive,
              isStreaming,
              isAwaitingResponse: awaitingResponse,
              shouldShowRetryBanner: shouldShowRetryBanner(retryPending, websocketAvailable),
              placeholder: textPlaceholder,
              inputRef,
              isInFujiTour,
              attachedFiles,
              attachedFilesActions,
              isDragActive: isFileDropEnabled ? isDragActive : false,
            }}
          >
            {renderChatHeader()}
            {renderContent()}
            {renderInput()}
          </ChatProvider>
        </div>
      </Dialog>
    )
  }

  const getChatWindowClass = () => {
    let baseClass
    if (isMobile) {
      baseClass = classes.chatWindowMobile
    } else if (anchor === 'bottom') {
      baseClass = classes.chatWindowBottom
    } else if (anchor === 'left') {
      baseClass = shouldElevate ? classes.chatWindowSideLeftElevated : classes.chatWindowSideLeft
    } else {
      baseClass = shouldElevate ? classes.chatWindowSideElevated : classes.chatWindowSide
    }

    const classNames = [baseClass]
    if (voiceSessionActive) {
      classNames.push(classes.chatWindowVoiceMode)
    }
    return classNames.join(' ')
  }

  // Get dynamic style for voice mode height
  const getVoiceModeStyle = () => {
    if (!voiceSessionActive) return {}
    return {
      height: `${voiceModeHeight}px`,
      minHeight: `${voiceModeHeight}px`,
      maxHeight: `${voiceModeHeight}px`,
    }
  }

  const paperClass = isMobile
    ? classes.drawerPaperMobile
    : ['top', 'bottom'].includes(anchor)
      ? shouldElevate && anchor === 'bottom'
        ? classes.drawerPaperBottomElevated
        : classes.drawerPaperBottom
      : classes.drawerPaperSide

  return (
    <div className={classes.root}>
      <Drawer
        className={['top', 'bottom'].includes(anchor) ? classes.drawerBottom : classes.drawerSide}
        variant="persistent"
        anchor={anchor}
        open={chatDrawerOpen}
        sx={chatDrawerSx}
        PaperProps={{
          id: 'chatContainer',
          className: paperClass,
          elevation: 0,
          sx: chatDrawerPaperSx,
        }}
      >
        <div
          className={`${classes.chatWindow} ${getChatWindowClass()} ${
            isFileDropEnabled && isDragActive ? classes.chatWindowDragActive : ''
          }`}
          style={getVoiceModeStyle()}
          {...(isFileDropEnabled ? getRootProps() : {})}
        >
          {isFileDropEnabled && isDragActive && (
            <div className={classes.dragOverlayFull}>
              <div className={classes.dragOverlayText}>
                <CloudUploadIcon style={{ fontSize: 24 }} />
                Drop files here to upload
              </div>
            </div>
          )}
          <ChatProvider
            value={{
              messageText,
              setMessageText,
              onKeyDown: handleKeyDown,
              onSubmit: () =>
                sendMessage(osAssistantCode, messageText, setMessageText, attachedFiles, attachedFilesActions),
              onRetry: handleRetrySend,
              textInputEnabled,
              sendButtonEnabled,
              websocketAvailable,
              voiceSessionActive,
              isStreaming,
              isAwaitingResponse: awaitingResponse,
              shouldShowRetryBanner: shouldShowRetryBanner(retryPending, websocketAvailable),
              placeholder: textPlaceholder,
              inputRef,
              isInFujiTour,
              attachedFiles,
              attachedFilesActions,
              isDragActive: isFileDropEnabled ? isDragActive : false,
            }}
          >
            {renderChatHeader()}
            {renderContent()}
            {renderInput()}
            <TourBanner tourType="ada_chat" />
          </ChatProvider>
        </div>
      </Drawer>
    </div>
  )
}

export default ChatDrawer
